import {createTheme} from '@mui/material/styles';
import {breakpoints, colors, fontFamily, fontSize, fontWeight, shadows} from '@/styles/tokens';

const theme = createTheme({
    // Breakpoints come from tokens so `sx` media queries and the `sizes`
    // attributes built by `imgSizes()` share one definition. Values are
    // identical to MUI's defaults, so this is behaviour-neutral today.
    breakpoints: {values: breakpoints},
    palette: {
        primary: {
            main: colors.primary.main,
            dark: colors.primary.dark,
            light: colors.primary.light,
            contrastText: colors.text.inverse
        },
        text: {
            primary: colors.text.primary,
            secondary: colors.text.secondary
        },
        background: {
            default: colors.background.default,
            paper: colors.background.default
        }
    },
    shadows: [
        'none',
        shadows.sm,
        shadows.lg,
        shadows.lg,
        shadows.lg,
        shadows.ring,
        shadows.header,
        // MUI requires 25 shadow levels — fill remaining with the largest defined shadow
        ...new Array(18).fill(shadows.lg)
    ] as any,
    typography: {
        fontFamily: fontFamily.body,
        // ─── Heading defaults ─────────────────────────────────────────────────
        // Only h1 and h3 carry a size here, matching what the components on this
        // site were built against — h2/h4/h5/h6 deliberately have none, so those
        // variants inherit the browser default and every consumer keeps setting
        // its own `fontSize`. Do not reintroduce a full h1–h6 scale without
        // re-checking every page: components that rely on their own sx were sized
        // around these defaults, and adding sizes underneath them changes headings
        // site-wide.
        h1: {
            fontFamily: fontFamily.heading,
            fontSize: fontSize[48],
            fontWeight: fontWeight.bold
        },
        h2: {
            fontFamily: fontFamily.heading
        },
        h3: {
            fontFamily: fontFamily.heading,
            fontSize: fontSize[22],
            fontWeight: fontWeight.bold
        },
        h4: {
            fontFamily: fontFamily.heading
        },
        h5: {
            fontFamily: fontFamily.heading
        },
        h6: {
            fontFamily: fontFamily.heading
        },
        subtitle1: {
            fontFamily: fontFamily.body
        },
        subtitle2: {
            fontFamily: fontFamily.body
        },
        body1: {
            fontFamily: fontFamily.body
        },
        body2: {
            fontFamily: fontFamily.body
        },
        button: {
            fontFamily: fontFamily.body
        },
        caption: {
            fontFamily: fontFamily.body
        },
        overline: {
            fontFamily: fontFamily.body
        }
    },
    components: {
        // Base link styling for MUI <Link> — mirrors the global `a` defaults
        // (CSS system color keyword) so MUI-rendered links stay consistent.
        // No `&:visited` override: a visited rule would outrank component-level
        // `color` props/sx and flip links to the browser's purple. The `root`
        // color covers all states; component overrides win by specificity.
        MuiLink: {
            styleOverrides: {
                root: {
                    color: 'LinkText',
                    textDecoration: 'underline',
                    fontWeight: fontWeight.regular
                }
            }
        },
        // Body font (RegularText) on MUI components that would otherwise inherit their own default
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily: fontFamily.body
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-input': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiInputLabel-root': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiFormHelperText-root': {
                        fontFamily: fontFamily.body
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontFamily: fontFamily.body
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontFamily: fontFamily.body
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                message: {
                    fontFamily: fontFamily.body
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiDialogTitle-root': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiDialogContent-root': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiDialogActions-root': {
                        fontFamily: fontFamily.body
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    '& .MuiCardContent-root': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiCardHeader-title': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiCardHeader-subheader': {
                        fontFamily: fontFamily.body
                    }
                }
            }
        },
        MuiList: {
            styleOverrides: {
                root: {
                    '& .MuiListItem-root': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiListItemText-primary': {
                        fontFamily: fontFamily.body
                    },
                    '& .MuiListItemText-secondary': {
                        fontFamily: fontFamily.body
                    }
                }
            }
        }
    }
});

export default theme;
